import { useLocation as useAccountLocation } from "@Light/components/page/account";
import { SupportBottomSheetProvider } from "@Light/components/page/SupportBottomSheet";
import { useLight } from "@Light/services/light";
import type { PlanResponse } from "@Light/services/lightTypes";
import {
  LocationPlan,
  LocationRequest,
  PlanType,
} from "@Light/services/lightTypes";
import { addDays, parseLocalDate } from "@Light/utils/dateHelpers";
import { useMutation } from "@Light/utils/mutation";
import { useEffect, useLayoutEffect, useState } from "react";
import { RenewFlow } from "./flow/RenewFlow";
import type { RenewCustomerState } from "./flow/types";
import { renewScreens } from "./screens";

export type { RenewCustomerState } from "./flow/types";

const isPlanFallback = (plan: LocationPlan): boolean =>
  plan.plan_type === PlanType.Fallback ||
  (!plan.plan_type && plan.term_months === 1);

/**
 * Returns true if a renewal plan started within the last 14 days. This is determined by looking for
 * more than one TERM plan with the active one starting recently.
 *
 * TERM detection prefers explicit plan_type when present and falls back to term_months != 1.
 */
const didStartRecently = (plans?: LocationPlan[]) => {
  if (!plans) {
    return false;
  }

  const totalPlans = plans.length;
  if (totalPlans < 2) {
    return false;
  }

  const activePlan = plans.find(
    (plan) => plan.is_active && !isPlanFallback(plan),
  );
  if (!activePlan) {
    return false;
  }
  const contractStart = parseLocalDate(activePlan.contract_start);

  const today = new Date();
  const fourteenDaysAgo = addDays(today, -14);
  return contractStart >= fourteenDaysAgo;
};

export type RenewProps = {
  noRenewalScreen?: React.ReactNode;
  onLogout: (reason?: string) => void;
};

export const Renew: React.FC<RenewProps> = ({ onLogout }) => {
  const { uuid } = useAccountLocation();
  const { useGetLocationPlansQuery, useRequestRenewalPlansMutation } =
    useLight();
  const plans = useGetLocationPlansQuery({ uuid });
  const requestRates = useMutation<LocationRequest, PlanResponse[]>(
    useRequestRenewalPlansMutation,
  );

  const [initialPlansCount, setInitialPlansCount] = useState(0);

  useEffect(() => {
    if (requestRates.isUninitialized) {
      requestRates.mutate({ uuid });
    }
  }, [requestRates, uuid]);

  const plansData = plans.data?.data;

  const activePlan = plansData?.find((plan) => plan.is_active);
  const queuedPlan = plansData?.find((plan) => plan.is_queued);

  useLayoutEffect(() => {
    if (plansData && plansData.length > 0 && initialPlansCount === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitialPlansCount(plansData.length);
    }
  }, [plansData, initialPlansCount]);

  if (plans.isError) {
    return <div>Error loading plan</div>;
  }

  if (!activePlan) {
    return null;
  }

  const activeContractStart = parseLocalDate(activePlan.contract_start);

  // if we have more plans than we did before, then assume we just renewed in this session
  const justRenewed =
    initialPlansCount > 0 && plansData && plansData.length > initialPlansCount;

  // or if there are no renewal plans and the active contract started in the last 14 days, renewed recently
  const renewedRecently =
    !requestRates?.data?.length && didStartRecently(plansData);

  const activeIsFallback = isPlanFallback(activePlan);
  const isRecentTermRenewal =
    (justRenewed || renewedRecently) && !activeIsFallback;

  let customerState: RenewCustomerState;
  let transitionDate: Date | null = null;
  if (queuedPlan && !isPlanFallback(queuedPlan)) {
    customerState = "already_renewed";
    transitionDate = parseLocalDate(queuedPlan.contract_start);
  } else if (isRecentTermRenewal) {
    customerState = "renewed_recently";
    transitionDate = activeContractStart;
  } else if (activeIsFallback) {
    customerState = "renewal_from_fallback";
  } else {
    customerState = "renewal_from_term";
  }

  const earlyTerminationFee = parseFloat(
    activePlan.early_termination_fee_dollars,
  );

  const expirationDate = parseLocalDate(activePlan.contract_end);

  // noOffers is true if we are done fetching rates and there are no rates
  const noOffers = Boolean(
    !requestRates?.data?.length && requestRates.isSuccess,
  );

  return (
    <div className="box-border h-full overflow-hidden">
      <SupportBottomSheetProvider>
        <RenewFlow
          basePath="renew"
          screens={renewScreens}
          customerState={customerState}
          expirationDate={expirationDate}
          earlyTerminationFee={earlyTerminationFee}
          noOffers={noOffers}
          onLogout={onLogout}
          currentPlan={activePlan}
          transitionDate={transitionDate}
          isRecent={renewedRecently}
          justRenewed={Boolean(justRenewed)}
        />
      </SupportBottomSheetProvider>
    </div>
  );
};
